import { Component } from "react";
import autoBind from "auto-bind";
import withStyles from "@mui/styles/withStyles";
import { createStyles } from "@mui/styles";
import { connect } from "react-redux";
import {
  resendSignUpCode,
  signIn as amplifySignIn,
  confirmSignIn,
  signOut,
} from "aws-amplify/auth";
import { currentAuthenticatedUser } from "../helpers/auth";
import { SignInCard, PasswordChangeCard } from "../fathom-brella";
import { setCurrentUser } from "../redux/user/user-actions";
import history from "../helpers/history";
import { Typography, Button, Collapse } from "@mui/material";
import { ExpandMore as IconExpandMore } from "@mui/icons-material";
import { amber } from "@mui/material/colors";
import Header from "./Header";

const styles = theme =>
  createStyles({
    root: {
      background: "top / cover no-repeat url(/img/fathom-landing-bg.png)",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      fontFamily: "'Nunito Sans', sans-serif", // 20200414 JAB: I haven't figured out why the theme provider doesn't affect this page. This sets the font for now. (I even tried adding <MuiThemeProvider> directly, but no effect)
      overflow: "auto",
    },
    body: {
      background: "top / cover no-repeat url(/img/fathom-landing-bg.png)",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      justifyContent: "center",
      alignContent: "center",
      height: "100%",
      padding: theme.spacing(3),
    },
    signInCard: {
      alignContent: "center",
    },
    actions: {
      display: "flex",
    },
    workspaceInvite: {
      flexWrap: "wrap",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      borderRadius: 5,
      backgroundColor: `${amber[50]}`,
      boxShadow: `5px 5px 10px -5px ${theme.palette.primary.main}`,
      color: theme.palette.primary.dark,
      padding: theme.spacing(2),
      margin: theme.spacing(1.5),
      width: 314,
      height: 405,
      "& .wi-accept-container": {
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
      },
      "& .wi-accept-msg": {
        width: 180,
      },
      "& .wi-accept-email": {
        fontWeight: "bold",
        marginTop: theme.spacing(2),
        whiteSpace: "nowrap",
      },
      "& .wi-no-account": {
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
      },
      "& .wi-sign-up-content": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      },
      "& .wi-signup-button": {
        margin: theme.spacing(1),
      },
    },
    "@media (max-width: 723px)": {
      workspaceInvite: {
        height: "auto",
      },
    },
    expand: {
      transition: "all 250ms ease-out",
    },
    expandCollapse: {
      transform: "rotate(180deg)",
    },
  });

type State = {
  mode: "signIn" | "changePassword";
  error: string;
  loading: boolean;
  workspaceInvite: { id: string | null; email: string | null };
  noAccountExpanded: boolean;
};

class SignInPage extends Component<any, State> {
  constructor(props) {
    super(props);
    this.state = {
      mode: "signIn", // signIn || changePassword
      error: "",
      loading: false,
      workspaceInvite: { id: null, email: null },
      noAccountExpanded: false,
    };
    autoBind(this);
  }

  async componentDidMount() {
    // Check for workspace invitations and show info about it.
    const workspaceInvite = {
      id: localStorage.getItem("workspaceInviteId"),
      email: localStorage.getItem("workspaceInviteEmail"),
    };
    // keep the workspace invite clicked info in the state, since logout clears it:
    this.setState({ workspaceInvite });

    try {
      if (await currentAuthenticatedUser()) {
        history.push("/home");
      }
    } catch {
      // not an error, just not authenticated
    }
  }

  async completeSignIn() {
    const { workspaceInvite } = this.state;
    const cognitoUser = await currentAuthenticatedUser();

    await this.props.dispatch(setCurrentUser(cognitoUser));
    if (workspaceInvite.id) {
      localStorage.setItem("workspaceInviteId", workspaceInvite.id);
      localStorage.setItem("workspaceInviteEmail", workspaceInvite.email!);
      history.push("/workspaces");
    } else {
      history.push("/home");
    }
  }

  changeTempPassword(newPassword) {
    this.setState({ loading: true });
    confirmSignIn({ challengeResponse: newPassword })
      .then(() => this.completeSignIn())
      .catch(e => {
        console.error(e);
        this.setState({ error: e.message });
      })
      .finally(() => this.setState({ loading: false }));
  }

  cancelChangePassword() {
    // The user actually gets signed in when the temp password is correct
    signOut().finally(() => this.setState({ mode: "signIn" }));
  }

  signIn(email: string, password: string) {
    this.setState({ loading: true });

    amplifySignIn({ username: email.toLowerCase(), password })
      .then(signInOutput => {
        if (signInOutput.nextStep.signInStep == "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          this.setState({ mode: "changePassword" });
        } else if (signInOutput.nextStep.signInStep == "DONE") {
          this.completeSignIn();
        } else if (signInOutput.nextStep.signInStep == "RESET_PASSWORD") {
          // With aws-amplify v1, if the user was in the reset password state, this would
          // show up as a thrown error and be handled in the catch below. This error message
          // is just the same error message that would be attached to that error. I put this
          // in maintain the same behaviour, but we could add better handling for this case.
          this.setState({ error: "Password reset required for the user" });
          signOut();
        } else {
          console.error("Error logging user in with next step: ", signInOutput.nextStep.signInStep);
          this.setState({ error: "Error signing in, please contact administrator" });
          signOut();
        }
      })
      .catch(err => {
        if (err.code === "UserNotConfirmedException") {
          this.setState({
            error:
              "Your email is not verified, please check your email again for a new verification email",
          });
          resendSignUpCode({
            username: email.toLowerCase(),
            options: {
              // Some extra metadata to build the email verification link
              clientMetadata: { origin: window.location.origin },
            },
          }).catch(e => this.setState({ error: e.message }));
        } else {
          this.setState({ error: err.message });
        }
      })
      .finally(() => this.setState({ loading: false }));
  }

  forgotPassword(email) {
    let redir = "/forgot-password";

    if (email) {
      redir += `?email=${email}`;
    }

    history.push(redir);
  }

  setNoAccountExpanded(noAccountExpanded) {
    this.setState({ noAccountExpanded });
  }

  render() {
    const { classes } = this.props;
    const { mode, error, loading, workspaceInvite, noAccountExpanded } = this.state;

    return (
      <div className={classes.root}>
        <Header classes={classes} />
        <div className={classes.body}>
          {mode === "signIn" ? (
            <>
              {workspaceInvite.id ? (
                <div className={classes.workspaceInvite}>
                  <Typography align="center" variant="h6">
                    You have been invited to a shared Fathom workspace!
                  </Typography>
                  <div className="wi-accept-container">
                    <Typography align="center" className="wi-accept-msg">
                      To accept the invitation, please log in as:
                    </Typography>
                    <Typography align="center" className="wi-accept-email">
                      {workspaceInvite.email}
                    </Typography>
                  </div>
                  <div
                    className="wi-no-account"
                    onClick={() => this.setNoAccountExpanded(!noAccountExpanded)}
                  >
                    <Typography variant="subtitle2" align="center">
                      Don't have a Fathom account yet?
                    </Typography>
                    <IconExpandMore
                      className={`${classes.expand} ${
                        noAccountExpanded ? classes.expandCollapse : ""
                      }`}
                    />
                  </div>
                  <Collapse in={noAccountExpanded} classes={{ wrapperInner: "wi-sign-up-content" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      className="wi-signup-button"
                      onClick={() =>
                        history.push(
                          `/signup?workspaceInviteId=${workspaceInvite.id}&workspaceInviteEmail=${workspaceInvite.email}`
                        )
                      }
                    >
                      Register
                    </Button>
                    <Typography variant="subtitle2" align="center">
                      to accept the invitation.
                    </Typography>
                  </Collapse>
                </div>
              ) : null}
              <div className={classes.signInCard}>
                <SignInCard
                  logoUrl="img/fathom-central.png"
                  onSignIn={this.signIn}
                  errorMsg={error}
                  loading={loading}
                  onForgotPassword={email => this.forgotPassword(email)}
                />
              </div>
            </>
          ) : (
            <PasswordChangeCard
              onSubmit={this.changeTempPassword}
              onCancel={this.cancelChangePassword}
              loading={loading}
            />
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(connect()(SignInPage));
